import { useEffect, useState } from 'react'

import store from '../store'
import { getAssetRules, open, startPolling, stopPolling, updateWidgetsWithAlarms } from '../actions'
import { HIGHEST_SEVERITY, SECOND_HIGHEST_SEVERITY } from '../constants'
import { getHighestSeverity } from '../utils'

const useStore = () => {
    const [storeState, setStoreState] = useState(store.getState())

    useEffect(() => {
        const unsubscribeHandler = store.subscribe(() => {
            setStoreState(store.getState())
        })

        return unsubscribeHandler
    }, [])

    return storeState
}

const channelAlarmsService = {
    useSoundAlarms: (widgetId, channels) => {
        const storeState = useStore()

        useEffect(() => {
            channels.forEach(channel => {
                const channelAlarms = (storeState.dashboardReducer.batch || []).filter(
                    alarm => alarm.ref.mnemonic === channel
                )

                const alarmSeverity = getHighestSeverity(channelAlarms)
                if (alarmSeverity === SECOND_HIGHEST_SEVERITY || alarmSeverity === HIGHEST_SEVERITY) {
                    if (widgetId) {
                        store.dispatch(updateWidgetsWithAlarms(widgetId, channel, channelAlarms))
                    }
                } else if (!alarmSeverity && widgetId) {
                    store.dispatch(updateWidgetsWithAlarms(widgetId, channel, channelAlarms, true))
                }
            })
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [storeState.dashboardReducer.batch, JSON.stringify(channels), widgetId])
    },
    useAlarmRules: () => {
        const [rules, setRules] = useState({ userRules: [], globalRules: [] })
        const storeState = useStore()

        useEffect(() => {
            setRules({
                userRules: storeState.editorReducer.assetRules.user
                    .filter(r => r.active)
                    .map(r => ({ ...r, channel: r.conditions[0].channel })),
                globalRules: storeState.editorReducer.assetRules.global
                    .filter(r => r.active)
                    .map(r => ({ ...r, channel: r.conditions[0].channel }))
            })
        }, [storeState.editorReducer.assetRules])

        return rules
    },
    useAssetAlarms: () => {
        const [alarms, setAlarms] = useState([])
        const storeState = useStore()

        useEffect(() => {
            setAlarms(storeState.dashboardReducer.batch)
        }, [storeState.dashboardReducer.batch])

        return alarms
    },
    openSideview: channel => {
        if (Object.keys(store.getState().editorReducer.curves).indexOf(channel) >= 0) {
            store.dispatch(open(channel))
        }
    },
    useStore,
    useAlarmPolling: assetName => {
        const storeState = useStore()
        const [alarms, setAlarms] = useState([])

        useEffect(() => {
            const abortController = new AbortController()

            if (assetName) {
                store.dispatch(startPolling(assetName, null, abortController))

                return () => {
                    abortController.abort()
                    store.dispatch(stopPolling())
                }
            }
        }, [assetName])

        useEffect(() => {
            setAlarms(storeState.dashboardReducer.batch)
        }, [storeState.dashboardReducer.batch])

        return alarms
    },
    getRules: abortController => {
        store.dispatch(getAssetRules(abortController))
    }
}

export default channelAlarmsService
